import { useEffect, useState, ChangeEvent, FocusEvent, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { Accordion, Card, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { InfoCircleFill, Trash, Plus, Pencil, Check, X, ExclamationTriangleFill, UnlockFill } from 'react-bootstrap-icons';
import classnames from 'classnames';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ToastContainer } from 'react-toastify';

import './OrganisationTours.scss';
import { defaultScoringValues, scoringPresets } from './scoringPresets';
import type { ScoringValues } from './scoringPresets';
import {
  useFindByTour,
  useCreate as useCreateLeaderboard,
  useUpdate as useUpdateLeaderboard,
  useRemove as useRemoveLeaderboard,
} from '../../generated/orval/admin';
import { CustomAccordionHeader } from '../tourDetails/common/components/CustomAccordionHeader';
import { useGetCurrentOrganization } from '../../features/organizations/organizationsSelectors';
import { getOrganizationTours, createTour, updateTour } from '../../apis/tours';
import { fetchTournamentsData } from '../../apis/tournament';
import { TournamentsState } from '../../features/tournaments/tournamentsSlice';
import { CustomButton } from '../../components';
import { BackIcon } from '../../assets/icons/BackIcon';
import { CheckIcon } from '../../assets/icons/CheckIcon';
import { InfoImageUpload } from '../tourDetails/Information/components/InfoImageUpload';
import { Notification } from '../../hooks/useNotification';
import { TypeError } from '../../types/enums';
import type { components } from '../../generated/api-types';
import { GroupStandingsTieBreakerEnum } from '../../types/enums';
import { GroupStandingsTieBreakerSection } from '../tourDetails/common/components/GroupStandingsTieBreakerEditor';
import {
  DEFAULT_TEAM_MATCHUP_SCORING,
  hasRequiredGroupStandingsTieBreakers,
  requiredGroupStandingsTieBreakersErrorKey,
  resolveGroupStandingsTieBreakerResolution,
  TeamMatchupScoring,
  toPersistedTeamMatchupScoring,
} from '../../utils/groupStandingsTieBreakers';

type AdminOrganizationTourDto = components['schemas']['AdminOrganizationTourDto'];
type AdminTourOrganizationAccessDto = components['schemas']['AdminTourOrganizationAccessDto'];


interface ExtendedTour
  extends Omit<AdminOrganizationTourDto, 'groupStandingsTieBreakers'> {
  abbreviation?: string;
  icon?: string | null;
  scoringValuesArray: ScoringValues[];
  leaderboardWindowWeeks?: number | null;
  leaderboardMaxTournaments: number | null;
  rankGroupPlacement: boolean;
  groupStandingsTieBreakers: GroupStandingsTieBreakerEnum[];
  useInheritedGroupStandingsTieBreakers: boolean;
  teamMatchupScoring: TeamMatchupScoring;
  useInheritedTeamMatchupScoring: boolean;
}

interface ValidationErrors {
  name?: string;
  abbreviation?: string;
  icon?: string;
  groupStandingsTieBreakers?: string;
}

// Utility functions for scoring values
const toNumber = (val: number | string): number =>
  val === '' ? 0 : Number(val);

const resolveInheritedTourTieBreakers = (
  organizationTieBreakers?: GroupStandingsTieBreakerEnum[] | null,
): GroupStandingsTieBreakerEnum[] =>
  resolveGroupStandingsTieBreakerResolution({
    organizationTieBreakers: organizationTieBreakers ?? null,
  }).effective;

// Fixed bracket groupings for placement points
// Each group represents array indices that share the same placement points
type PlacementBracketGroup = {
  label: string;           // e.g., "5-8" or "1st"
  startIndex: number;      // First array index in this group
  endIndex: number;        // Last array index in this group (inclusive)
  isMedal: 'gold' | 'silver' | 'bronze' | null;
};

// Single elimination brackets: 1st, 2nd, 3rd, 4th, 5-8, 9-16, 17-32, ...
const getSingleElimBrackets = (arrayLength: number, ordSuffix: string): PlacementBracketGroup[] => {
  const groups: PlacementBracketGroup[] = [];
  let index = 0;

  // First 4 positions are individual
  for (let i = 0; i < 4 && index < arrayLength - 1; i++) {
    groups.push({
      label: `${i + 1}${ordSuffix}`,
      startIndex: index,
      endIndex: index,
      isMedal: i === 0 ? 'gold' : i === 1 ? 'silver' : i === 2 ? 'bronze' : null,
    });
    index++;
  }

  // Remaining positions follow power of 2 pattern: 5-8 (4 slots), 9-16 (8 slots), 17-32 (16 slots), ...
  let groupSize = 4; // 5-8 has 4 positions
  let posStart = 5;

  while (index < arrayLength - 1) {
    const endIndex = Math.min(index + groupSize - 1, arrayLength - 2);
    const posEnd = posStart + (endIndex - index);
    groups.push({
      label: `${posStart}-${posEnd}`,
      startIndex: index,
      endIndex: endIndex,
      isMedal: null,
    });
    index = endIndex + 1;
    posStart = posEnd + 1;
    groupSize *= 2;
  }

  return groups;
};

// Double elimination brackets: 1st, 2nd, 3rd, 4th, 5-6, 7-8, 9-12, 13-16, 17-24, 25-32, ...
const getDoubleElimBrackets = (arrayLength: number, ordSuffix: string): PlacementBracketGroup[] => {
  const groups: PlacementBracketGroup[] = [];
  let index = 0;

  // First 4 positions are individual
  for (let i = 0; i < 4 && index < arrayLength - 1; i++) {
    groups.push({
      label: `${i + 1}${ordSuffix}`,
      startIndex: index,
      endIndex: index,
      isMedal: i === 0 ? 'gold' : i === 1 ? 'silver' : i === 2 ? 'bronze' : null,
    });
    index++;
  }

  // Double elim pattern: pairs of groups that double in size
  // 5-6, 7-8 (size 2 each), then 9-12, 13-16 (size 4 each), then 17-24, 25-32 (size 8 each), ...
  let groupSize = 2;
  let posStart = 5;

  while (index < arrayLength - 1) {
    // Two groups of same size
    for (let pair = 0; pair < 2 && index < arrayLength - 1; pair++) {
      const endIndex = Math.min(index + groupSize - 1, arrayLength - 2);
      const posEnd = posStart + (endIndex - index);
      groups.push({
        label: `${posStart}-${posEnd}`,
        startIndex: index,
        endIndex: endIndex,
        isMedal: null,
      });
      index = endIndex + 1;
      posStart = posEnd + 1;
    }
    groupSize *= 2;
  }

  return groups;
};

export const OrganisationTours = () => {
  const prefix = 'organisationTours-';
  const { t } = useTranslation();
  const currentOrganization = useGetCurrentOrganization();
  const queryClient = useQueryClient();

  const [tours, setTours] = useState<AdminOrganizationTourDto[]>([]);
  const [tournaments, setTournaments] = useState<TournamentsState[]>([]);
  const [tournamentsLoaded, setTournamentsLoaded] = useState(false);
  const [selectedTour, setSelectedTour] = useState<ExtendedTour | null>(null);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [isTourDropdownOpen, setIsTourDropdownOpen] = useState(false);
  const tourDropdownRef = useRef<HTMLDivElement>(null);
  const [isOwner, setIsOwner] = useState(false);
  const [newLeaderboardName, setNewLeaderboardName] = useState('');
  const [editingLeaderboardId, setEditingLeaderboardId] = useState<number | null>(null);
  const [editLeaderboardName, setEditLeaderboardName] = useState('');
  const [isCustomizing, setIsCustomizing] = useState(false);
  const [hasCustomized, setHasCustomized] = useState(false);
  const [selectedPresetId, setSelectedPresetId] = useState<string | null>(null);
  const [activeScoringIndex, setActiveScoringIndex] = useState(0);
  const [tourFormData, setTourFormData] = useState<ExtendedTour>({
    id: 0,
    name: '',
    description: null,
    abbreviation: '',
    icon: null,
    colorTop: '#2C2F30',
    colorBottom: '#909798',
    longDescription: null,
    logo: null,
    type: 1,
    sortOrder: null,
    discoverable: false,
    tournaments: [],
    organizations: [],
    scoringValuesArray: [{ ...defaultScoringValues }],
    leaderboardWindowDays: null,
    leaderboardWindowWeeks: null,
    leaderboardMaxTournaments: null,
    rankGroupPlacement: false,
    groupStandingsTieBreakers: resolveInheritedTourTieBreakers(),
    useInheritedGroupStandingsTieBreakers: true,
    teamMatchupScoring: DEFAULT_TEAM_MATCHUP_SCORING,
    useInheritedTeamMatchupScoring: true,
  });

  useEffect(() => {
    if (currentOrganization?.id) {
      //loadTours();
      loadTournaments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrganization?.id]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (tourDropdownRef.current && !tourDropdownRef.current.contains(event.target as Node)) {
        setIsTourDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const { data: toursData } = useQuery({
    queryKey: ['organizationTours', currentOrganization?.id],
    queryFn: () => getOrganizationTours(currentOrganization!.id),
    enabled: !!currentOrganization?.id,
  });

  // Leaderboard hooks
  const { data: leaderboards, refetch: refetchLeaderboards } = useFindByTour(
    selectedTour?.id?.toString() ?? '',
    { query: { enabled: !!selectedTour?.id && !isCreatingNew } }
  );
  const createLeaderboardMutation = useCreateLeaderboard();
  const updateLeaderboardMutation = useUpdateLeaderboard();
  const deleteLeaderboardMutation = useRemoveLeaderboard();

  useEffect(() => {
    setEditingLeaderboardId(null);
    setEditLeaderboardName('');
    setNewLeaderboardName('');
  }, [selectedTour?.id, isOwner]);

  useEffect(() => {
    if (toursData?.items) {
      setTours(toursData.items);
    }
  }, [toursData]);

  const loadTournaments = async () => {
    if (!currentOrganization?.id) return;
    try {
      const orgTournaments = await fetchTournamentsData(currentOrganization.id);
      setTournaments(orgTournaments);
    } catch (error) {
      console.error('Failed to load tournaments:', error);
    } finally {
      setTournamentsLoaded(true);
    }
  };

  const isCurrentOrgOwner = (tourId: number): boolean => {
    if (!currentOrganization?.id || !toursData?.items) return false;
    const tourFromData = toursData.items.find((t: AdminOrganizationTourDto) => t.id === tourId);
    if (!tourFromData?.organizations) return false;
    const orgAccess = tourFromData.organizations.find(
      (org: AdminTourOrganizationAccessDto) => org.id === currentOrganization.id
    );
    return orgAccess?.isOwner ?? false;
  };

  const parseScoringValues = (raw: any): ScoringValues[] => {
    if (!raw || !Array.isArray(raw) || raw.length === 0) {
      return [{ ...defaultScoringValues }];
    }
    const safeParse = (sv: any) => {
      if (typeof sv !== 'string') return sv ?? {};
      try {
        return JSON.parse(sv);
      } catch {
        return {};
      }
    };
    const ensureRemaining = (arr?: number[]) =>
      Array.isArray(arr) && arr.length > 0 ? arr : [0];
    return raw.map((sv: any) => {
      const parsed = safeParse(sv);
      return {
        rankingType: parsed.rankingType ?? 100,
        participation: parsed.participation ?? 0,
        matchWinWB: parsed.matchWinWB ?? 0,
        matchWinLB: parsed.matchWinLB ?? 0,
        matchWinGroup: parsed.matchWinGroup ?? 0,
        matchLoseWB: parsed.matchLoseWB ?? 0,
        matchLoseLB: parsed.matchLoseLB ?? 0,
        matchLoseGroup: parsed.matchLoseGroup ?? 0,
        singleElimPlacement: ensureRemaining(parsed.singleElimPlacement),
        doubleElimPlacement: ensureRemaining(parsed.doubleElimPlacement),
      };
    });
  };

  const detectMatchingPreset = (
    values: ScoringValues[],
    windowWeeks: number | null | undefined,
    maxTournaments: number | null | undefined,
    rankGroupPlacement: boolean = false,
  ): string | null => {
    // Convert to number for comparison (empty string = 0)
    const toNum = (val: number | string): number => (val === '' ? 0 : Number(val));

    const arraysEqual = (a: (number | string)[], b: (number | string)[]) => {
      const aNorm = a.map(toNum);
      const bNorm = b.map(toNum);
      return aNorm.length === bNorm.length && aNorm.every((v, i) => v === bNorm[i]);
    };

    const valuesEqual = (a: ScoringValues, b: ScoringValues) =>
      toNum(a.rankingType) === toNum(b.rankingType) &&
      toNum(a.participation) === toNum(b.participation) &&
      toNum(a.matchWinWB) === toNum(b.matchWinWB) &&
      toNum(a.matchWinLB) === toNum(b.matchWinLB) &&
      toNum(a.matchWinGroup) === toNum(b.matchWinGroup) &&
      toNum(a.matchLoseWB) === toNum(b.matchLoseWB) &&
      toNum(a.matchLoseLB) === toNum(b.matchLoseLB) &&
      toNum(a.matchLoseGroup) === toNum(b.matchLoseGroup) &&
      arraysEqual(a.singleElimPlacement, b.singleElimPlacement) &&
      arraysEqual(a.doubleElimPlacement, b.doubleElimPlacement);

    for (const preset of scoringPresets) {
      if (preset.values.length !== values.length) continue;
      if ((preset.leaderboardWindowWeeks ?? null) !== (windowWeeks ?? null)) continue;
      if ((preset.leaderboardMaxTournaments ?? null) !== (maxTournaments ?? null)) continue;
      if ((preset.rankGroupPlacement ?? false) !== (rankGroupPlacement ?? false)) continue;
      const allMatch = preset.values.every((pv, i) => valuesEqual(values[i], pv));
      if (allMatch) return preset.id;
    }
    return null;
  };

  const handleTourSelect = (tourId: string) => {
    setValidationErrors({});
    setActiveScoringIndex(0);

    if (tourId === 'new') {
      setIsCreatingNew(true);
      setIsOwner(true);
      setSelectedTour(null);
      setSelectedPresetId('RAQT');
      setHasCustomized(false);
      setTourFormData({
        id: 0,
        name: '',
        description: null,
        abbreviation: '',
        icon: null,
        colorTop: '#2C2F30',
        colorBottom: '#909798',
        longDescription: null,
        logo: null,
        type: 1,
        sortOrder: null,
        discoverable: false,
        tournaments: [],
        organizations: [],
        scoringValuesArray: [{ ...defaultScoringValues }],
        leaderboardWindowDays: null,
        leaderboardWindowWeeks: null,
        leaderboardMaxTournaments: null,
        rankGroupPlacement: false,
        groupStandingsTieBreakers: resolveInheritedTourTieBreakers(
          currentOrganization?.groupStandingsTieBreakers,
        ),
        useInheritedGroupStandingsTieBreakers: true,
        teamMatchupScoring: DEFAULT_TEAM_MATCHUP_SCORING,
        useInheritedTeamMatchupScoring: true,
      });
    } else {
      setIsCreatingNew(false);
      const tour = tours.find((t) => t.id === Number(tourId));
      if (tour) {
        setIsOwner(isCurrentOrgOwner(tour.id));

        // Map backend fields to frontend fields:
        // Backend description -> Frontend name (actual tour name)
        // Backend name -> Frontend abbreviation
        // Backend longDescription -> Frontend description
        const scoringValues = parseScoringValues(tour.scoringValues);
        const tieBreakers = (
          tour as { groupStandingsTieBreakers?: GroupStandingsTieBreakerEnum[] | null }
        ).groupStandingsTieBreakers;
        const tourScoring = (
          tour as { teamMatchupScoring?: TeamMatchupScoring | null }
        ).teamMatchupScoring;
        const inheritedTieBreakers = resolveInheritedTourTieBreakers(
          currentOrganization?.groupStandingsTieBreakers,
        );
        const extendedTour: ExtendedTour = {
          ...tour,
          name: tour.description || '',
          abbreviation: tour.name || '',
          description: tour.longDescription || '',
          icon: tour.logo || null,
          colorTop: tour.colorTop || '#2C2F30',
          colorBottom: tour.colorBottom || '#909798',
          scoringValuesArray: scoringValues,
          leaderboardWindowWeeks: tour.leaderboardWindowDays
            ? Math.round(tour.leaderboardWindowDays / 7)
            : null,
          leaderboardMaxTournaments: tour.leaderboardMaxTournaments ?? null,
          rankGroupPlacement: tour.rankGroupPlacement ?? false,
          groupStandingsTieBreakers: tieBreakers?.length
            ? tieBreakers
            : inheritedTieBreakers,
          useInheritedGroupStandingsTieBreakers: !tieBreakers?.length,
          teamMatchupScoring: tourScoring ?? DEFAULT_TEAM_MATCHUP_SCORING,
          useInheritedTeamMatchupScoring: tourScoring == null,
        };
        setSelectedTour(extendedTour);
        setTourFormData(extendedTour);
        const matchedPreset = detectMatchingPreset(scoringValues, extendedTour.leaderboardWindowWeeks, extendedTour.leaderboardMaxTournaments, extendedTour.rankGroupPlacement);
        setSelectedPresetId(matchedPreset);
        setHasCustomized(!matchedPreset);
      }
    }
  };

  const handleInput = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setTourFormData((prev) => ({
      ...prev,
      [name]: name === 'abbreviation' ? value.trim().toUpperCase().slice(0, 4) : value,
    }));
  };

  const activeScoringConfig =
    tourFormData.scoringValuesArray[activeScoringIndex] || defaultScoringValues;
  const tourTieBreakerResolution = resolveGroupStandingsTieBreakerResolution({
    tourTieBreakers: tourFormData.useInheritedGroupStandingsTieBreakers
      ? null
      : tourFormData.groupStandingsTieBreakers,
    organizationTieBreakers: currentOrganization?.groupStandingsTieBreakers ?? null,
    tourTeamMatchupScoring: tourFormData.useInheritedTeamMatchupScoring
      ? null
      : tourFormData.teamMatchupScoring,
    organizationTeamMatchupScoring: currentOrganization?.teamMatchupScoring ?? null,
  });

  const selectOnFocus = (e: FocusEvent<HTMLInputElement>) => e.target.select();

  const handleScoringInput = (field: keyof ScoringValues, value: string) => {
    // Allow empty string, otherwise parse as number
    if (value !== '') {
      const numValue = parseInt(value, 10);
      if (isNaN(numValue) || numValue < 0) return;
    }
    setHasCustomized(true);
    setSelectedPresetId(null);
    setTourFormData((prev) => {
      const newArray = [...prev.scoringValuesArray];
      newArray[activeScoringIndex] = {
        ...newArray[activeScoringIndex],
        [field]: value === '' ? '' : parseInt(value, 10),
      };
      return { ...prev, scoringValuesArray: newArray };
    });
  };

  const handlePlacementChange = (
    field: 'singleElimPlacement' | 'doubleElimPlacement',
    index: number,
    value: string
  ) => {
    // Allow empty string, otherwise parse as number
    if (value !== '') {
      const numValue = parseInt(value, 10);
      if (isNaN(numValue) || numValue < 0) return;
    }
    setHasCustomized(true);
    setSelectedPresetId(null);
    setTourFormData((prev) => {
      const newArray = [...prev.scoringValuesArray];
      const currentConfig = newArray[activeScoringIndex];
      const updatedPlacement = [...currentConfig[field]];
      updatedPlacement[index] = value === '' ? '' : parseInt(value, 10);
      newArray[activeScoringIndex] = { ...currentConfig, [field]: updatedPlacement };
      return { ...prev, scoringValuesArray: newArray };
    });
  };

  // Get the next bracket size for adding positions
  const getNextBracketSize = (currentLength: number, fieldType: 'group' | 'elim'): number => {
    // currentLength includes the "remaining" slot, so actual positions = currentLength - 1
    const positions = currentLength - 1;

    if (positions < 4) {
      // Still in individual positions (1st, 2nd, 3rd, 4th)
      return 1;
    }

    if (fieldType === 'group') {
      // Single elim: 4 positions for 5-8, 8 for 9-16, 16 for 17-32, etc.
      // After positions 4-7 (indices 4-7), next is 8-15, then 16-31, etc.
      if (positions < 8) return 8 - positions; // Fill up to 8
      if (positions < 16) return 16 - positions; // Fill up to 16
      if (positions < 32) return 32 - positions; // Fill up to 32
      if (positions < 64) return 64 - positions;
      if (positions < 128) return 128 - positions;
      return 1; // Fallback
    } else {
      // Double elim: pairs of 2, then pairs of 4, then pairs of 8, etc.
      // 4-5 (5-6), 6-7 (7-8), 8-11 (9-12), 12-15 (13-16), etc.
      if (positions < 6) return 6 - positions;
      if (positions < 8) return 8 - positions;
      if (positions < 12) return 12 - positions;
      if (positions < 16) return 16 - positions;
      if (positions < 24) return 24 - positions;
      if (positions < 32) return 32 - positions;
      if (positions < 48) return 48 - positions;
      if (positions < 64) return 64 - positions;
      if (positions < 96) return 96 - positions;
      if (positions < 128) return 128 - positions;
      return 1; // Fallback
    }
  };

  const handleAddPlacement = (field: 'singleElimPlacement' | 'doubleElimPlacement') => {
    setHasCustomized(true);
    setSelectedPresetId(null);
    setTourFormData((prev) => {
      const newArray = [...prev.scoringValuesArray];
      const currentConfig = newArray[activeScoringIndex];
      const currentPlacement = currentConfig[field];
      const remainingValue = currentPlacement[currentPlacement.length - 1];

      // Calculate how many positions to add for the next bracket
      const fieldType = field === 'singleElimPlacement' ? 'group' : 'elim';
      const bracketSize = getNextBracketSize(currentPlacement.length, fieldType);

      // Insert new positions before the remaining slot
      const newPositions = Array(bracketSize).fill(remainingValue);
      const updatedPlacement = [
        ...currentPlacement.slice(0, -1),
        ...newPositions,
        remainingValue,
      ];
      newArray[activeScoringIndex] = { ...currentConfig, [field]: updatedPlacement };
      return { ...prev, scoringValuesArray: newArray };
    });
  };

  const handleRemovePlacement = (field: 'singleElimPlacement' | 'doubleElimPlacement', index: number) => {
    setHasCustomized(true);
    setSelectedPresetId(null);
    setTourFormData((prev) => {
      const newArray = [...prev.scoringValuesArray];
      const currentConfig = newArray[activeScoringIndex];
      const currentPlacement = currentConfig[field];
      // Don't allow removing if only 2 items left (1st place + remaining)
      if (currentPlacement.length <= 2) return prev;
      const updatedPlacement = currentPlacement.filter((_, i) => i !== index);
      newArray[activeScoringIndex] = { ...currentConfig, [field]: updatedPlacement };
      return { ...prev, scoringValuesArray: newArray };
    });
  };

  // Handle changing value for a bracket group (updates all indices in the bracket)
  const handleBracketPlacementChange = (
    field: 'singleElimPlacement' | 'doubleElimPlacement',
    startIndex: number,
    endIndex: number,
    value: string
  ) => {
    if (value !== '') {
      const numValue = parseInt(value, 10);
      if (isNaN(numValue) || numValue < 0) return;
    }
    setHasCustomized(true);
    setSelectedPresetId(null);
    setTourFormData((prev) => {
      const newArray = [...prev.scoringValuesArray];
      const currentConfig = newArray[activeScoringIndex];
      const updatedPlacement = [...currentConfig[field]];
      const newValue = value === '' ? '' : parseInt(value, 10);
      for (let i = startIndex; i <= endIndex; i++) {
        updatedPlacement[i] = newValue;
      }
      newArray[activeScoringIndex] = { ...currentConfig, [field]: updatedPlacement };
      return { ...prev, scoringValuesArray: newArray };
    });
  };

  // Remove a bracket group (removes all indices in the bracket)
  const handleRemoveBracketGroup = (
    field: 'singleElimPlacement' | 'doubleElimPlacement',
    startIndex: number,
    endIndex: number
  ) => {
    setHasCustomized(true);
    setSelectedPresetId(null);
    setTourFormData((prev) => {
      const newArray = [...prev.scoringValuesArray];
      const currentConfig = newArray[activeScoringIndex];
      const currentPlacement = currentConfig[field];
      const groupSize = endIndex - startIndex + 1;
      // Don't allow removing if it would leave only remaining
      if (currentPlacement.length - groupSize < 2) return prev;
      const updatedPlacement = currentPlacement.filter(
        (_, i) => i < startIndex || i > endIndex
      );
      newArray[activeScoringIndex] = { ...currentConfig, [field]: updatedPlacement };
      return { ...prev, scoringValuesArray: newArray };
    });
  };

  const handleApplyPreset = (presetId: string) => {
    if (hasCustomized) {
      const matchesPreset = detectMatchingPreset(
        tourFormData.scoringValuesArray,
        tourFormData.leaderboardWindowWeeks,
        tourFormData.leaderboardMaxTournaments,
        tourFormData.rankGroupPlacement,
      );
      if (!matchesPreset && !window.confirm(t(prefix + 'presetOverwriteWarning'))) {
        return;
      }
    }
    const preset = scoringPresets.find((p) => p.id === presetId);
    if (preset) {
      setTourFormData((prev) => ({
        ...prev,
        scoringValuesArray: preset.values.map((v) => ({ ...v })),
        leaderboardWindowDays: preset.leaderboardWindowWeeks != null ? preset.leaderboardWindowWeeks * 7 : null,
        leaderboardWindowWeeks: preset.leaderboardWindowWeeks,
        leaderboardMaxTournaments: preset.leaderboardMaxTournaments,
        rankGroupPlacement: preset.rankGroupPlacement,
      }));
      setActiveScoringIndex(0);
      setIsCustomizing(false);
      setHasCustomized(false);
        setSelectedPresetId(presetId);
    }
  };

  const handleAddScoringConfig = () => {
    setHasCustomized(true);
    setSelectedPresetId(null);
    const newIndex = tourFormData.scoringValuesArray.length;
    setTourFormData((prev) => ({
      ...prev,
      scoringValuesArray: [...prev.scoringValuesArray, { ...defaultScoringValues, rankingType: 0 }],
    }));
    setActiveScoringIndex(newIndex);
  };

  const handleRemoveScoringConfig = (index: number) => {
    setHasCustomized(true);
    setSelectedPresetId(null);
    setTourFormData((prev) => {
      const newArray = prev.scoringValuesArray.filter((_, i) => i !== index);
      return { ...prev, scoringValuesArray: newArray };
    });
    setActiveScoringIndex((prev) => (prev >= index && prev > 0 ? prev - 1 : prev));
  };

  const handleRankingTypeChange = (value: string) => {
    // Allow empty string, otherwise parse as number
    if (value !== '') {
      const numValue = parseInt(value, 10);
      if (isNaN(numValue) || numValue < 0) return;
    }
    setHasCustomized(true);
    setSelectedPresetId(null);
    setTourFormData((prev) => {
      const newArray = [...prev.scoringValuesArray];
      newArray[activeScoringIndex] = {
        ...newArray[activeScoringIndex],
        rankingType: value === '' ? '' : parseInt(value, 10),
      };
      return { ...prev, scoringValuesArray: newArray };
    });
  };

  const validateForm = (): boolean => {
    const errors: ValidationErrors = {};

    if (!tourFormData.name || tourFormData.name.trim() === '') {
      errors.name = t(prefix + 'nameRequired');
    }

    if (isCreatingNew && (!tourFormData.abbreviation || tourFormData.abbreviation.trim() === '')) {
      errors.abbreviation = t(prefix + 'abbreviationRequired');
    }

    if (!tourFormData.icon) {
      errors.icon = t(prefix + 'imageRequired');
    }

    if (
      !tourFormData.useInheritedGroupStandingsTieBreakers &&
      !hasRequiredGroupStandingsTieBreakers(tourFormData.groupStandingsTieBreakers)
    ) {
      const errorKey = requiredGroupStandingsTieBreakersErrorKey(
        tourFormData.groupStandingsTieBreakers,
      );
      errors.groupStandingsTieBreakers = t(errorKey);
      Notification(TypeError.error, t(errorKey));
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    if (isCreatingNew) {
      try {
        // Prepare the data for the API
        // Map frontend fields to backend fields:
        // Frontend name -> Backend description (actual tour name)
        // Frontend abbreviation -> Backend name
        // Frontend description -> Backend longDescription
        const tourData = {
          description: tourFormData.name,
          name: tourFormData.abbreviation || undefined,
          longDescription: tourFormData.description || undefined,
          logo: tourFormData.icon || undefined,
          colorTop: tourFormData.colorTop || '#2C2F30',
          colorBottom: tourFormData.colorBottom || '#909798',
          discoverable: tourFormData.discoverable ?? false,
          rankGroupPlacement: tourFormData.rankGroupPlacement ?? false,
          ownerOrganizationId: currentOrganization?.id,
          type: 1,
          scoringValues: tourFormData.scoringValuesArray.map((sv) => ({
            rankingType: toNumber(sv.rankingType),
            participation: toNumber(sv.participation),
            matchWinWB: toNumber(sv.matchWinWB),
            matchWinLB: toNumber(sv.matchWinLB),
            matchWinGroup: toNumber(sv.matchWinGroup),
            matchLoseWB: toNumber(sv.matchLoseWB),
            matchLoseLB: toNumber(sv.matchLoseLB),
            matchLoseGroup: toNumber(sv.matchLoseGroup),
            singleElimPlacement: sv.singleElimPlacement.map(toNumber),
            doubleElimPlacement: sv.doubleElimPlacement.map(toNumber),
          })),
          leaderboardWindowDays: tourFormData.leaderboardWindowWeeks != null ? tourFormData.leaderboardWindowWeeks * 7 : null,
          leaderboardMaxTournaments: tourFormData.leaderboardMaxTournaments ?? null,
          groupStandingsTieBreakers: tourFormData.useInheritedGroupStandingsTieBreakers
            ? null
            : tourFormData.groupStandingsTieBreakers,
          teamMatchupScoring: toPersistedTeamMatchupScoring(
            tourFormData.useInheritedTeamMatchupScoring,
            tourFormData.teamMatchupScoring,
          ),
        };

        const newTour = await createTour(tourData);

        // Invalidate and refetch the tours list
        await queryClient.invalidateQueries({
          queryKey: ['organizationTours', currentOrganization?.id],
        });

        // Show success notification
        Notification(TypeError.success, t(prefix + 'tourCreated'));

        // Select the newly created tour directly from API response
        if (newTour?.id) {
          setIsCreatingNew(false);
          setIsOwner(true);
          const tourWithGroupStandings = newTour as {
            groupStandingsTieBreakers?: GroupStandingsTieBreakerEnum[] | null;
            teamMatchupScoring?: TeamMatchupScoring | null;
          };
          const inheritedTieBreakers = resolveInheritedTourTieBreakers(
            currentOrganization?.groupStandingsTieBreakers,
          );
          const extendedTour: ExtendedTour = {
            ...newTour,
            name: newTour.description || '',
            abbreviation: newTour.name || '',
            description: newTour.longDescription || '',
            icon: newTour.logo || null,
            colorTop: newTour.colorTop || '#2C2F30',
            colorBottom: newTour.colorBottom || '#909798',
            scoringValuesArray: parseScoringValues(newTour.scoringValues),
            leaderboardWindowWeeks: newTour.leaderboardWindowDays ? Math.round(newTour.leaderboardWindowDays / 7) : null,
            leaderboardMaxTournaments: newTour.leaderboardMaxTournaments ?? null,
            rankGroupPlacement: newTour.rankGroupPlacement ?? false,
            groupStandingsTieBreakers: tourWithGroupStandings.groupStandingsTieBreakers?.length
              ? tourWithGroupStandings.groupStandingsTieBreakers
              : inheritedTieBreakers,
            useInheritedGroupStandingsTieBreakers: !tourWithGroupStandings.groupStandingsTieBreakers?.length,
            teamMatchupScoring:
              tourWithGroupStandings.teamMatchupScoring ?? DEFAULT_TEAM_MATCHUP_SCORING,
            useInheritedTeamMatchupScoring: tourWithGroupStandings.teamMatchupScoring == null,
          };
          setSelectedTour(extendedTour);
          setTourFormData(extendedTour);
        } else {
          setIsCreatingNew(false);
        }
      } catch (error) {
        console.error('Failed to create tour:', error);
        Notification(TypeError.error, t(prefix + 'tourCreateError'));
      }
    } else {
      try {
        // Prepare the data for the API
        // Map frontend fields to backend fields:
        // Frontend name -> Backend description (actual tour name)
        // Frontend abbreviation -> Backend name
        // Frontend description -> Backend longDescription
        const tourData = {
          description: tourFormData.name,
          name: tourFormData.abbreviation || undefined,
          longDescription: tourFormData.description || undefined,
          logo: tourFormData.icon || undefined,
          colorTop: tourFormData.colorTop || '#2C2F30',
          colorBottom: tourFormData.colorBottom || '#909798',
          discoverable: tourFormData.discoverable ?? false,
          rankGroupPlacement: tourFormData.rankGroupPlacement ?? false,
          scoringValues: tourFormData.scoringValuesArray.map((sv) => ({
            rankingType: toNumber(sv.rankingType),
            participation: toNumber(sv.participation),
            matchWinWB: toNumber(sv.matchWinWB),
            matchWinLB: toNumber(sv.matchWinLB),
            matchWinGroup: toNumber(sv.matchWinGroup),
            matchLoseWB: toNumber(sv.matchLoseWB),
            matchLoseLB: toNumber(sv.matchLoseLB),
            matchLoseGroup: toNumber(sv.matchLoseGroup),
            singleElimPlacement: sv.singleElimPlacement.map(toNumber),
            doubleElimPlacement: sv.doubleElimPlacement.map(toNumber),
          })),
          leaderboardWindowDays: tourFormData.leaderboardWindowWeeks != null ? tourFormData.leaderboardWindowWeeks * 7 : null,
          leaderboardMaxTournaments: tourFormData.leaderboardMaxTournaments ?? null,
          groupStandingsTieBreakers: tourFormData.useInheritedGroupStandingsTieBreakers
            ? null
            : tourFormData.groupStandingsTieBreakers,
          teamMatchupScoring: toPersistedTeamMatchupScoring(
            tourFormData.useInheritedTeamMatchupScoring,
            tourFormData.teamMatchupScoring,
          ),
        };

        await updateTour(tourFormData.id, tourData);

        // Invalidate and refetch the tours list
        await queryClient.invalidateQueries({
          queryKey: ['organizationTours', currentOrganization?.id],
        });

        // Show success notification
        Notification(TypeError.success, t(prefix + 'tourUpdated'));

        // Update the selected tour with new data
        setSelectedTour({ ...tourFormData });
      } catch (error: any) {
        console.error('Failed to update tour:', error);
        const backendMessage = error?.response?.data?.message;
        Notification(
          TypeError.error,
          backendMessage || t(prefix + 'tourUpdateError')
        );
      }
    }
  };

  const getTournamentsForTour = (tourId: number | null): AdminOrganizationTourDto['tournaments'] => {
    if (!tourId) return [];
    const tour = toursData?.items?.find((t: AdminOrganizationTourDto) => t.id === tourId);
    return tour?.tournaments ?? [];
  };

  const isOwnTournament = (tournamentId: number) => {
    return tournaments.some((t) => t.id === tournamentId);
  };

  // Leaderboard handlers
  const handleCreateLeaderboard = async () => {
    if (!newLeaderboardName.trim() || !selectedTour?.id) {
      Notification(TypeError.error, t(prefix + 'leaderboardNameRequired'));
      return;
    }

    try {
      await createLeaderboardMutation.mutateAsync({
        data: {
          name: newLeaderboardName.trim(),
          tourId: selectedTour.id,
        },
      });
      setNewLeaderboardName('');
      refetchLeaderboards();
      Notification(TypeError.success, t(prefix + 'leaderboardCreated'));
    } catch (error) {
      console.error('Failed to create leaderboard:', error);
      Notification(TypeError.error, t(prefix + 'leaderboardCreateError'));
    }
  };

  const handleUpdateLeaderboard = async (id: number) => {
    if (!editLeaderboardName.trim()) {
      Notification(TypeError.error, t(prefix + 'leaderboardNameRequired'));
      return;
    }

    try {
      await updateLeaderboardMutation.mutateAsync({
        id: id.toString(),
        data: { name: editLeaderboardName.trim() },
      });
      setEditingLeaderboardId(null);
      setEditLeaderboardName('');
      refetchLeaderboards();
      Notification(TypeError.success, t(prefix + 'leaderboardUpdated'));
    } catch (error) {
      console.error('Failed to update leaderboard:', error);
      Notification(TypeError.error, t(prefix + 'leaderboardUpdateError'));
    }
  };

  const handleDeleteLeaderboard = async (id: number) => {
    if (!window.confirm(t(prefix + 'deleteLeaderboardConfirm'))) {
      return;
    }

    try {
      await deleteLeaderboardMutation.mutateAsync({ id: id.toString() });
      refetchLeaderboards();
      Notification(TypeError.success, t(prefix + 'leaderboardDeleted'));
    } catch (error) {
      console.error('Failed to delete leaderboard:', error);
      Notification(TypeError.error, t(prefix + 'leaderboardDeleteError'));
    }
  };

  const startEditLeaderboard = (lb: { id: number; name: string }) => {
    setEditingLeaderboardId(lb.id);
    setEditLeaderboardName(lb.name);
  };

  const cancelEditLeaderboard = () => {
    setEditingLeaderboardId(null);
    setEditLeaderboardName('');
  };

  if (!currentOrganization) {
    return (
      <div className="organisation-tours">
        <div className="organisation-tours__title">{t(prefix + 'title')}</div>
        <div className="organisation-tours__error">Organization not found</div>
      </div>
    );
  }

  return (
    <div className="organisation-tours-wrapper">
      <div className="organisation-tours-header px-2">
        <div className="d-flex align-items-center my-3">
          <NavLink
            to={`/community/${currentOrganization.id}`}
            className="d-flex align-items-center organisation-tours-header__back-link"
          >
            <BackIcon />
          </NavLink>
          <h1 className="font-weight-bold my-2 organisation-tours-header__title">
            {t(prefix + 'title')}
          </h1>
          {(selectedTour || isCreatingNew) && isOwner && (
            <button
              type="button"
              className="organisation-tours-header__save-button"
              onClick={handleSave}
            >
              <span>{isCreatingNew ? t(prefix + 'createTour') : t(prefix + 'save')}</span>
              <CheckIcon />
            </button>
          )}
        </div>
      </div>
      <div className="organisation-tours">
        <div className="organisation-tours__section">
          <label className="organisation-tours__label">{t(prefix + 'selectTour')}</label>
          <div className="organisation-tours__dropdown" ref={tourDropdownRef}>
            <button
              type="button"
              className="organisation-tours__dropdown-button"
              onClick={() => setIsTourDropdownOpen(!isTourDropdownOpen)}
            >
              {isCreatingNew ? (
                <div className="organisation-tours__dropdown-selected">
                  <span className="organisation-tours__role-dot organisation-tours__role-dot--owner"></span>
                  <span>{t(prefix + 'createNewTour')}</span>
                </div>
              ) : selectedTour ? (
                <div className="organisation-tours__dropdown-selected">
                  <span
                    className={classnames('organisation-tours__role-dot', {
                      'organisation-tours__role-dot--owner': isOwner,
                      'organisation-tours__role-dot--manager': !isOwner,
                    })}
                  ></span>
                  <span>{selectedTour.name || `Tour ${selectedTour.id}`}</span>
                </div>
              ) : (
                <span>{t(prefix + 'selectTourPlaceholder')}</span>
              )}
              <span className="organisation-tours__dropdown-arrow">
                {isTourDropdownOpen ? '▲' : '▼'}
              </span>
            </button>
            {isTourDropdownOpen && (
              <ul className="organisation-tours__dropdown-list">
                <li
                  className="organisation-tours__dropdown-item"
                  onClick={() => {
                    handleTourSelect('new');
                    setIsTourDropdownOpen(false);
                  }}
                >
                  {t(prefix + 'createNewTour')}
                </li>
                {tours.map((tour) => {
                  const isOwner = isCurrentOrgOwner(tour.id);
                  return (
                    <li
                      key={tour.id}
                      className={classnames('organisation-tours__dropdown-item', {
                        'organisation-tours__dropdown-item--selected': selectedTour?.id === tour.id,
                      })}
                      onClick={() => {
                        handleTourSelect(tour.id.toString());
                        setIsTourDropdownOpen(false);
                      }}
                    >
                      <span
                        className={classnames('organisation-tours__role-dot', {
                          'organisation-tours__role-dot--owner': isOwner,
                          'organisation-tours__role-dot--manager': !isOwner,
                        })}
                      ></span>
                      <span>{tour.description || tour.name || `Tour ${tour.id}`}</span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        {(selectedTour || isCreatingNew) && (
          <>
            <div className="organisation-tours__section-header">
              <h3 className="organisation-tours__section-title">{t(prefix + 'tourInfo')}</h3>
            </div>

            {!isCreatingNew && !isOwner && (
              <div className="organisation-tours__permission-info">
                <div className="organisation-tours__permission-info-content">
                  <span className="organisation-tours__role-dot organisation-tours__role-dot--manager"></span>
                  <span>{t(prefix + 'managerPermission')}</span>
                </div>
              </div>
            )}
            <Card>
              <Card.Body className="organisation-tours__tool-body">
                <div className="organisation-tours__form">
                  <div className="organisation-tours__form-section">
                    <label className="organisation-tours__label">{t(prefix + 'name')}</label>
                    <Form.Control
                      autoComplete="off"
                      name="name"
                      type="text"
                      value={tourFormData.name}
                      onChange={handleInput}
                      placeholder={t(prefix + 'namePlaceholder')}
                      className={validationErrors.name ? 'is-invalid' : ''}
                      disabled={!isOwner}
                    />
                    {validationErrors.name && (
                      <div className="organisation-tours__error">{validationErrors.name}</div>
                    )}
                  </div>

                  <div className="organisation-tours__form-section">
                    <label className="organisation-tours__label">
                      {t(prefix + 'abbreviation')}
                    </label>
                    <Form.Control
                      autoComplete="off"
                      name="abbreviation"
                      type="text"
                      value={tourFormData.abbreviation || ''}
                      onChange={handleInput}
                      placeholder={t(prefix + 'abbreviationPlaceholder')}
                      maxLength={4}
                      disabled={!isOwner}
                      className={validationErrors.abbreviation ? 'is-invalid' : ''}
                    />
                    {validationErrors.abbreviation && (
                      <div className="organisation-tours__error">{validationErrors.abbreviation}</div>
                    )}
                  </div>

                  <div className="organisation-tours__form-section">
                    <div className="organisation-tours__image-label-wrapper">
                      <span className="organisation-tours__label">{t(prefix + 'image')}</span>
                      <span className="organisation-tours__image-tip">
                        {t(prefix + 'imageTip')}
                      </span>
                    </div>
                    <div
                      className="organisation-tours__image-preview"
                      style={{
                        background: `linear-gradient(to bottom, ${tourFormData.colorTop || '#2C2F30'}, ${tourFormData.colorBottom || '#909798'})`,
                      }}
                    >
                      <div
                        style={{
                          opacity: isOwner ? 1 : 0.6,
                          pointerEvents: isOwner ? 'auto' : 'none',
                        }}
                      >
                        <InfoImageUpload
                          setFieldValue={(field, value) => {
                            setTourFormData((prev) => ({ ...prev, icon: value }));
                            if (validationErrors.icon) {
                              setValidationErrors((prev) => ({ ...prev, icon: undefined }));
                            }
                          }}
                          imgUrl={tourFormData.icon || null}
                          settingData={{
                            width: '200',
                            height: '200',
                            name: 'icon',
                            type: 'img',
                          }}
                          savePath="tournament"
                          disabled={!isOwner}
                        />
                      </div>
                    </div>

                    {validationErrors.icon && (
                      <div className="organisation-tours__error">{validationErrors.icon}</div>
                    )}
                  </div>

                  <div className="organisation-tours__form-section">
                    <label className="organisation-tours__label">{t(prefix + 'description')}</label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="description"
                      value={tourFormData.description || ''}
                      onChange={handleInput}
                      placeholder={t(prefix + 'descriptionPlaceholder')}
                      disabled={!isOwner}
                    />
                  </div>

                  <div className="organisation-tours__form-section">
                    <div className="organisation-tours__colors-row">
                      <div className="organisation-tours__color-item">
                        <label className="organisation-tours__label">
                          {t(prefix + 'colorTop')}
                        </label>
                        <Form.Control
                          autoComplete="off"
                          name="colorTop"
                          type="color"
                          value={tourFormData.colorTop || '#2C2F30'}
                          onChange={handleInput}
                          disabled={!isOwner}
                          className="form-control-color"
                        />
                      </div>
                      <div className="organisation-tours__color-item">
                        <label className="organisation-tours__label">
                          {t(prefix + 'colorBottom')}
                        </label>
                        <Form.Control
                          autoComplete="off"
                          name="colorBottom"
                          type="color"
                          value={tourFormData.colorBottom || '#909798'}
                          onChange={handleInput}
                          disabled={!isOwner}
                          className="form-control-color"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="organisation-tours__form-section">
                    <label className="organisation-tours__label">
                      {t(prefix + 'active')}
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>{t(prefix + 'activeInfo')}</Tooltip>}
                      >
                        <InfoCircleFill
                          size={14}
                          className="organisation-tours__info-icon"
                        />
                      </OverlayTrigger>
                    </label>
                    <div className="organisation-tours__public-toggle">
                      <button
                        type="button"
                        className={classnames('organisation-tours__public-button', {
                          'organisation-tours__public-button--active':
                            tourFormData.discoverable === true,
                        })}
                        onClick={() => setTourFormData((prev) => ({ ...prev, discoverable: true }))}
                        disabled={!isOwner}
                      >
                        {t(prefix + 'yes')}
                      </button>
                      <button
                        type="button"
                        className={classnames('organisation-tours__public-button', {
                          'organisation-tours__public-button--active':
                            tourFormData.discoverable === false,
                        })}
                        onClick={() =>
                          setTourFormData((prev) => ({ ...prev, discoverable: false }))
                        }
                        disabled={!isOwner}
                      >
                        {t(prefix + 'no')}
                      </button>
                    </div>
                  </div>

                  {/* <div className="organisation-tours__form-section">
                  <label className="organisation-tours__label">{t(prefix + 'owner')}</label>
                  {(() => {
                    let ownerName: string | null = null;
                    if (isCreatingNew && currentOrganization) {
                      ownerName = currentOrganization.name;
                    } else if (selectedTour) {
                      ownerName = getOwnerOrganization(selectedTour.id);
                    }

                    return ownerName ? (
                      <div className="organisation-tours__owner-display">
                        <span className="organisation-tours__role-dot organisation-tours__role-dot--owner"></span>
                        <span>{ownerName}</span>
                      </div>
                    ) : (
                      <div className="organisation-tours__error">{t(prefix + 'ownerNotFound')}</div>
                    );
                  })()}
                </div> */}

                </div>
              </Card.Body>
            </Card>

            <Accordion>
              <Card>
                <Card.Header className="p-0 bg-white border-0 relative">
                  <CustomAccordionHeader eventKey="scoringSystem">
                    {t(prefix + 'scoringSystem')}
                  </CustomAccordionHeader>
                </Card.Header>
                <Accordion.Collapse eventKey="scoringSystem">
                  <Card.Body className="organisation-tours__tool-body">
                    <div className="organisation-tours__preset-section">
                      <div className="organisation-tours__preset-header">
                        <label className="organisation-tours__label">
                          {t(prefix + 'choosePreset')}
                        </label>
                        <button
                          type="button"
                          className={classnames('organisation-tours__custom-toggle', {
                            'organisation-tours__custom-toggle--selected': isCustomizing,
                          })}
                          onClick={() => {
                            setIsCustomizing(true);
                            setSelectedPresetId(null);
                            setHasCustomized(true);
                                                  }}
                          disabled={!isOwner}
                        >
                          <UnlockFill size={12} />
                          {t(prefix + 'custom')}
                        </button>
                      </div>
                      <div className="organisation-tours__preset-cards">
                        {scoringPresets.map((preset) => (
                          <button
                            key={preset.id}
                            type="button"
                            className={classnames('organisation-tours__preset-card', {
                              'organisation-tours__preset-card--selected':
                                selectedPresetId === preset.id && !isCustomizing,
                            })}
                            onClick={() => handleApplyPreset(preset.id)}
                            disabled={!isOwner}
                          >
                            <span className="organisation-tours__preset-card-name">
                              {preset.name}
                            </span>
                            <span className="organisation-tours__preset-card-summary">
                              {preset.values.map((v) => v.rankingType).join(', ')}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="organisation-tours__preset-box">

                    <div className="organisation-tours__scoring-section-box">
                      <div className="organisation-tours__scoring-section-label">
                        {t(prefix + 'tourSettings')}
                      </div>
                    <div className="organisation-tours__scoring-row">
                      <div className="organisation-tours__form-section">
                        <label className="organisation-tours__label">
                          {t(prefix + 'leaderboardWindowWeeks')}
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>{t(prefix + 'leaderboardWindowWeeksTooltip')}</Tooltip>}
                          >
                            <InfoCircleFill
                              size={14}
                              className="organisation-tours__info-icon"
                            />
                          </OverlayTrigger>
                        </label>
                        <Form.Control
                          autoComplete="off"
                          name="leaderboardWindowWeeks"
                          type="number"
                          min="1"
                          value={tourFormData.leaderboardWindowWeeks ?? ''}
                          onChange={(e) => {
                            const val = e.target.value;
                            const parsed = val === '' ? null : parseInt(val, 10);
                            const normalized = parsed && !Number.isNaN(parsed) && parsed > 0 ? parsed : null;
                            setHasCustomized(true);
                            setSelectedPresetId(null);
                            setTourFormData((prev) => ({
                              ...prev,
                              leaderboardWindowDays: normalized != null ? normalized * 7 : null,
                              leaderboardWindowWeeks: normalized,
                            }));
                          }}
                          placeholder={t(prefix + 'leaderboardWindowWeeksPlaceholder')}
                          disabled={!isOwner || !isCustomizing}
                        />
                      </div>
                      <div className="organisation-tours__form-section">
                        <label className="organisation-tours__label">
                          {t(prefix + 'leaderboardMaxTournaments')}
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>{t(prefix + 'leaderboardMaxTournamentsTooltip')}</Tooltip>}
                          >
                            <InfoCircleFill
                              size={14}
                              className="organisation-tours__info-icon"
                            />
                          </OverlayTrigger>
                        </label>
                        <Form.Control
                          autoComplete="off"
                          name="leaderboardMaxTournaments"
                          type="number"
                          min="1"
                          value={tourFormData.leaderboardMaxTournaments ?? ''}
                          onChange={(e) => {
                            const val = e.target.value;
                            const parsed = val === '' ? null : parseInt(val, 10);
                            const normalized = parsed && !Number.isNaN(parsed) && parsed > 0 ? parsed : null;
                            setHasCustomized(true);
                                                    setSelectedPresetId(null);
                            setTourFormData((prev) => ({
                              ...prev,
                              leaderboardMaxTournaments: normalized,
                            }));
                          }}
                          placeholder={t(prefix + 'leaderboardMaxTournamentsPlaceholder')}
                          disabled={!isOwner || !isCustomizing}
                        />
                      </div>

                      <div className="organisation-tours__form-section">
                        <label className="organisation-tours__label">
                          {t(prefix + 'rankGroupPlacement')}
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>{t(prefix + 'rankGroupPlacementTooltip')}</Tooltip>}
                          >
                            <InfoCircleFill
                              size={14}
                              className="organisation-tours__info-icon"
                            />
                          </OverlayTrigger>
                        </label>
                        <div className="organisation-tours__public-toggle">
                          <button
                            type="button"
                            className={classnames('organisation-tours__public-button', {
                              'organisation-tours__public-button--active':
                                tourFormData.rankGroupPlacement === true,
                            })}
                            onClick={() => {
                              setHasCustomized(true);
                              setSelectedPresetId(null);
                              setTourFormData((prev) => ({ ...prev, rankGroupPlacement: true }));
                            }}
                            disabled={!isOwner || !isCustomizing}
                          >
                            {t(prefix + 'yes')}
                          </button>
                          <button
                            type="button"
                            className={classnames('organisation-tours__public-button', {
                              'organisation-tours__public-button--active':
                                tourFormData.rankGroupPlacement === false,
                            })}
                            onClick={() => {
                              setHasCustomized(true);
                              setSelectedPresetId(null);
                              setTourFormData((prev) => ({ ...prev, rankGroupPlacement: false }));
                            }}
                            disabled={!isOwner || !isCustomizing}
                          >
                            {t(prefix + 'no')}
                          </button>
                        </div>
                      </div>
                    </div>
                    </div>

                    {tourFormData.scoringValuesArray.length > 0 && (
                      <div className="organisation-tours__scoring-section-box">
                        <div className="organisation-tours__scoring-tabs-header">
                          <div className="organisation-tours__scoring-tabs-label">
                            <span>{t(prefix + 'rankingTypes')}</span>
                            <OverlayTrigger
                              placement="top"
                              overlay={<Tooltip>{t(prefix + 'rankingTypeInfo')}</Tooltip>}
                            >
                              <InfoCircleFill
                                size={14}
                                style={{ cursor: 'pointer', color: '#666' }}
                              />
                            </OverlayTrigger>
                          </div>
                        </div>
                        <div className="organisation-tours__scoring-tabs">
                          {tourFormData.scoringValuesArray.map((sv, index) => (
                            <div key={index} className="organisation-tours__scoring-tab-wrapper">
                              <button
                                type="button"
                                className={classnames('organisation-tours__scoring-tab', {
                                  'organisation-tours__scoring-tab--active':
                                    index === activeScoringIndex,
                                })}
                                onClick={() => setActiveScoringIndex(index)}
                              >
                                {sv.rankingType || '???'}
                              </button>
                              {isCustomizing &&
                                tourFormData.scoringValuesArray.length > 1 && (
                                  <button
                                    type="button"
                                    className="organisation-tours__scoring-tab-remove"
                                    onClick={() => handleRemoveScoringConfig(index)}
                                    title={t(prefix + 'removeRankingType')}
                                  >
                                    <X size={14} />
                                  </button>
                                )}
                            </div>
                          ))}
                          {isOwner && isCustomizing && (
                            <button
                              type="button"
                              className="organisation-tours__scoring-tab organisation-tours__scoring-tab--add"
                              onClick={handleAddScoringConfig}
                              title={t(prefix + 'addRankingType')}
                            >
                              <Plus size={16} />
                            </button>
                          )}
                        </div>

                        <div className="organisation-tours__tip">{t(prefix + 'scoringTip')}</div>
                        <div className="organisation-tours__scoring-section">
                          <div className="organisation-tours__scoring-row">
                            <div className="organisation-tours__form-section">
                              <label className="organisation-tours__label">
                                {t(prefix + 'rankingType')}
                                <OverlayTrigger
                                  placement="top"
                                  overlay={
                                    <Tooltip>
                                      {t(prefix + 'rankingTypeNameTooltip')}
                                    </Tooltip>
                                  }
                                >
                                  <InfoCircleFill
                                    size={14}
                                    className="organisation-tours__info-icon"
                                  />
                                </OverlayTrigger>
                              </label>
                              <Form.Control
                                type="number"
                                min="0"
                                value={activeScoringConfig.rankingType}
                                onChange={(e) => handleRankingTypeChange(e.target.value)}
                                onFocus={selectOnFocus}
                                disabled={!isOwner || !isCustomizing}
                              />
                            </div>
                            <div className="organisation-tours__form-section">
                              <label className="organisation-tours__label">
                                {t(prefix + 'participation')}
                                <OverlayTrigger
                                  placement="top"
                                  overlay={<Tooltip>{t(prefix + 'participationTooltip')}</Tooltip>}
                                >
                                  <InfoCircleFill
                                    size={14}
                                    className="organisation-tours__info-icon"
                                  />
                                </OverlayTrigger>
                              </label>
                              <Form.Control
                                type="number"
                                min="0"
                                value={activeScoringConfig.participation}
                                onChange={(e) =>
                                  handleScoringInput('participation', e.target.value)
                                }
                                onFocus={selectOnFocus}
                                disabled={!isOwner || !isCustomizing}
                              />
                            </div>
                          </div>

                          <div className="organisation-tours__form-section">
                            <label className="organisation-tours__label">
                              {t(prefix + 'matchPoints')}
                              <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip>{t(prefix + 'matchPointsTooltip')}</Tooltip>}
                              >
                                <InfoCircleFill
                                  size={14}
                                  className="organisation-tours__info-icon"
                                />
                              </OverlayTrigger>
                            </label>
                            <div className="organisation-tours__match-table">
                              <div className="organisation-tours__match-table-header">
                                <div className="organisation-tours__match-table-label" />
                                <div>{t(prefix + 'winnerBracket')}</div>
                                <div>{t(prefix + 'loserBracket')}</div>
                                <div>{t(prefix + 'groupStage')}</div>
                              </div>
                              <div className="organisation-tours__match-table-row">
                                <div className="organisation-tours__match-table-label">
                                  {t(prefix + 'win')}
                                </div>
                                <Form.Control
                                  type="number"
                                  min="0"
                                  value={activeScoringConfig.matchWinWB}
                                  onChange={(e) =>
                                    handleScoringInput('matchWinWB', e.target.value)
                                  }
                                  onFocus={selectOnFocus}
                                  disabled={!isOwner || !isCustomizing}
                                />
                                <Form.Control
                                  type="number"
                                  min="0"
                                  value={activeScoringConfig.matchWinLB}
                                  onChange={(e) =>
                                    handleScoringInput('matchWinLB', e.target.value)
                                  }
                                  onFocus={selectOnFocus}
                                  disabled={!isOwner || !isCustomizing}
                                />
                                <Form.Control
                                  type="number"
                                  min="0"
                                  value={activeScoringConfig.matchWinGroup}
                                  onChange={(e) =>
                                    handleScoringInput('matchWinGroup', e.target.value)
                                  }
                                  onFocus={selectOnFocus}
                                  disabled={!isOwner || !isCustomizing}
                                />
                              </div>
                              <div className="organisation-tours__match-table-row">
                                <div className="organisation-tours__match-table-label">
                                  {t(prefix + 'loss')}
                                </div>
                                <Form.Control
                                  type="number"
                                  min="0"
                                  value={activeScoringConfig.matchLoseWB}
                                  onChange={(e) =>
                                    handleScoringInput('matchLoseWB', e.target.value)
                                  }
                                  onFocus={selectOnFocus}
                                  disabled={!isOwner || !isCustomizing}
                                />
                                <Form.Control
                                  type="number"
                                  min="0"
                                  value={activeScoringConfig.matchLoseLB}
                                  onChange={(e) =>
                                    handleScoringInput('matchLoseLB', e.target.value)
                                  }
                                  onFocus={selectOnFocus}
                                  disabled={!isOwner || !isCustomizing}
                                />
                                <Form.Control
                                  type="number"
                                  min="0"
                                  value={activeScoringConfig.matchLoseGroup}
                                  onChange={(e) =>
                                    handleScoringInput('matchLoseGroup', e.target.value)
                                  }
                                  onFocus={selectOnFocus}
                                  disabled={!isOwner || !isCustomizing}
                                />
                              </div>
                            </div>
                          </div>

                          <div className="organisation-tours__placements-row">
                            <div className="organisation-tours__form-section">
                              <label className="organisation-tours__label">
                                {t(prefix + 'singleElimPlacement')}
                                <OverlayTrigger
                                  placement="top"
                                  overlay={
                                    <Tooltip>{t(prefix + 'singleElimPlacementTooltip')}</Tooltip>
                                  }
                                >
                                  <InfoCircleFill
                                    size={14}
                                    className="organisation-tours__info-icon"
                                  />
                                </OverlayTrigger>
                              </label>
                              <div className="organisation-tours__placement-list">
                                {(() => {
                                  const arr = activeScoringConfig.singleElimPlacement || [];
                                  const ord = t(prefix + 'placeOrdinal');
                                  const brackets = getSingleElimBrackets(arr.length, ord);
                                  const remainingIndex = arr.length - 1;

                                  return (
                                    <>
                                      {brackets.map((bracket, bracketIndex) => {
                                        const value = arr[bracket.startIndex];
                                        const medalClass = bracket.isMedal
                                          ? `organisation-tours__placement-item--${bracket.isMedal}`
                                          : '';
                                        // Check if value is higher than previous bracket
                                        const numValue = Number(value) || 0;
                                        const prevBracket = bracketIndex > 0 ? brackets[bracketIndex - 1] : null;
                                        const prevValue = prevBracket ? (Number(arr[prevBracket.startIndex]) || 0) : Infinity;
                                        const showWarning = numValue > prevValue;

                                        return (
                                          <div
                                            key={bracketIndex}
                                            className={`organisation-tours__placement-item ${medalClass} ${showWarning ? 'organisation-tours__placement-item--warning' : ''}`}
                                          >
                                            <span className="organisation-tours__placement-label">
                                              {bracket.label}
                                            </span>
                                            <Form.Control
                                              type="number"
                                              min="0"
                                              value={value}
                                              onChange={(e) =>
                                                handleBracketPlacementChange(
                                                  'singleElimPlacement',
                                                  bracket.startIndex,
                                                  bracket.endIndex,
                                                  e.target.value
                                                )
                                              }
                                              onFocus={selectOnFocus}
                                              disabled={!isOwner || !isCustomizing}
                                              className={showWarning ? 'organisation-tours__placement-input--warning' : ''}
                                            />
                                            {showWarning && (
                                              <OverlayTrigger
                                                placement="top"
                                                overlay={<Tooltip>{t(prefix + 'placementOrderWarning')}</Tooltip>}
                                              >
                                                <ExclamationTriangleFill
                                                  size={16}
                                                  className="organisation-tours__placement-warning-icon"
                                                />
                                              </OverlayTrigger>
                                            )}
                                            {isCustomizing && bracket.startIndex >= 4 && bracketIndex === brackets.length - 1 && (
                                              <button
                                                type="button"
                                                className="organisation-tours__placement-remove"
                                                onClick={() =>
                                                  handleRemoveBracketGroup('singleElimPlacement', bracket.startIndex, bracket.endIndex)
                                                }
                                              >
                                                <Trash size={16} />
                                              </button>
                                            )}
                                          </div>
                                        );
                                      })}
                                      {/* Remaining row */}
                                      {arr.length > 0 && (
                                        <div className="organisation-tours__placement-item">
                                          <span className="organisation-tours__placement-label">
                                            {t(prefix + 'remaining')}
                                            <OverlayTrigger
                                              placement="top"
                                              overlay={<Tooltip>{t(prefix + (tourFormData.rankGroupPlacement ? 'remainingInfoGroupOn' : 'remainingInfoGroupOff'))}</Tooltip>}
                                            >
                                              <InfoCircleFill
                                                size={12}
                                                className="organisation-tours__info-icon organisation-tours__info-icon--small"
                                              />
                                            </OverlayTrigger>
                                          </span>
                                          <Form.Control
                                            type="number"
                                            min="0"
                                            value={arr[remainingIndex]}
                                            onChange={(e) =>
                                              handlePlacementChange('singleElimPlacement', remainingIndex, e.target.value)
                                            }
                                            onFocus={selectOnFocus}
                                            onBlur={(e) => {
                                              if (e.target.value === '') {
                                                handlePlacementChange('singleElimPlacement', remainingIndex, '0');
                                              }
                                            }}
                                            disabled={!isOwner || !isCustomizing}
                                          />
                                        </div>
                                      )}
                                    </>
                                  );
                                })()}
                              </div>
                              {isCustomizing && isOwner && (activeScoringConfig.singleElimPlacement?.length || 0) < 129 && (
                                <button
                                  type="button"
                                  className="organisation-tours__placement-add"
                                  onClick={() => handleAddPlacement('singleElimPlacement')}
                                >
                                  <Plus size={18} /> {t(prefix + 'addPlacement')}
                                </button>
                              )}
                            </div>

                            <div className="organisation-tours__form-section">
                              <label className="organisation-tours__label">
                                {t(prefix + 'doubleElimPlacement')}
                                <OverlayTrigger
                                  placement="top"
                                  overlay={
                                    <Tooltip>{t(prefix + 'doubleElimPlacementTooltip')}</Tooltip>
                                  }
                                >
                                  <InfoCircleFill
                                    size={14}
                                    className="organisation-tours__info-icon"
                                  />
                                </OverlayTrigger>
                              </label>
                              <div className="organisation-tours__placement-list">
                                {(() => {
                                  const arr = activeScoringConfig.doubleElimPlacement || [];
                                  const ord = t(prefix + 'placeOrdinal');
                                  const brackets = getDoubleElimBrackets(arr.length, ord);
                                  const remainingIndex = arr.length - 1;

                                  return (
                                    <>
                                      {brackets.map((bracket, bracketIndex) => {
                                        const value = arr[bracket.startIndex];
                                        const medalClass = bracket.isMedal
                                          ? `organisation-tours__placement-item--${bracket.isMedal}`
                                          : '';
                                        // Check if value is higher than previous bracket
                                        const numValue = Number(value) || 0;
                                        const prevBracket = bracketIndex > 0 ? brackets[bracketIndex - 1] : null;
                                        const prevValue = prevBracket ? (Number(arr[prevBracket.startIndex]) || 0) : Infinity;
                                        const showWarning = numValue > prevValue;

                                        return (
                                          <div
                                            key={bracketIndex}
                                            className={`organisation-tours__placement-item ${medalClass} ${showWarning ? 'organisation-tours__placement-item--warning' : ''}`}
                                          >
                                            <span className="organisation-tours__placement-label">
                                              {bracket.label}
                                            </span>
                                            <Form.Control
                                              type="number"
                                              min="0"
                                              value={value}
                                              onChange={(e) =>
                                                handleBracketPlacementChange(
                                                  'doubleElimPlacement',
                                                  bracket.startIndex,
                                                  bracket.endIndex,
                                                  e.target.value
                                                )
                                              }
                                              onFocus={selectOnFocus}
                                              disabled={!isOwner || !isCustomizing}
                                              className={showWarning ? 'organisation-tours__placement-input--warning' : ''}
                                            />
                                            {showWarning && (
                                              <OverlayTrigger
                                                placement="top"
                                                overlay={<Tooltip>{t(prefix + 'placementOrderWarning')}</Tooltip>}
                                              >
                                                <ExclamationTriangleFill
                                                  size={16}
                                                  className="organisation-tours__placement-warning-icon"
                                                />
                                              </OverlayTrigger>
                                            )}
                                            {isCustomizing && bracket.startIndex >= 4 && bracketIndex === brackets.length - 1 && (
                                              <button
                                                type="button"
                                                className="organisation-tours__placement-remove"
                                                onClick={() =>
                                                  handleRemoveBracketGroup('doubleElimPlacement', bracket.startIndex, bracket.endIndex)
                                                }
                                              >
                                                <Trash size={16} />
                                              </button>
                                            )}
                                          </div>
                                        );
                                      })}
                                      {/* Remaining row */}
                                      {arr.length > 0 && (
                                        <div className="organisation-tours__placement-item">
                                          <span className="organisation-tours__placement-label">
                                            {t(prefix + 'remaining')}
                                            <OverlayTrigger
                                              placement="top"
                                              overlay={<Tooltip>{t(prefix + (tourFormData.rankGroupPlacement ? 'remainingInfoGroupOn' : 'remainingInfoGroupOff'))}</Tooltip>}
                                            >
                                              <InfoCircleFill
                                                size={12}
                                                className="organisation-tours__info-icon organisation-tours__info-icon--small"
                                              />
                                            </OverlayTrigger>
                                          </span>
                                          <Form.Control
                                            type="number"
                                            min="0"
                                            value={arr[remainingIndex]}
                                            onChange={(e) =>
                                              handlePlacementChange('doubleElimPlacement', remainingIndex, e.target.value)
                                            }
                                            onFocus={selectOnFocus}
                                            onBlur={(e) => {
                                              if (e.target.value === '') {
                                                handlePlacementChange('doubleElimPlacement', remainingIndex, '0');
                                              }
                                            }}
                                            disabled={!isOwner || !isCustomizing}
                                          />
                                        </div>
                                      )}
                                    </>
                                  );
                                })()}
                              </div>
                              {isCustomizing && isOwner && (activeScoringConfig.doubleElimPlacement?.length || 0) < 129 && (
                                <button
                                  type="button"
                                  className="organisation-tours__placement-add"
                                  onClick={() => handleAddPlacement('doubleElimPlacement')}
                                >
                                  <Plus size={18} /> {t(prefix + 'addPlacement')}
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    </div>
                  </Card.Body>
                </Accordion.Collapse>
              </Card>

              <Card>
                <Card.Header className="p-0 bg-white border-0 relative">
                  <CustomAccordionHeader eventKey="leaderboards">
                    {t(prefix + 'leaderboards')}
                  </CustomAccordionHeader>
                </Card.Header>
                <Accordion.Collapse eventKey="leaderboards">
                  <Card.Body className="organisation-tours__tool-body">
                    {!isCreatingNew && (
                      <>
                        {/* Create new leaderboard */}
                        {isOwner && (
                          <div className="organisation-tours__leaderboard-create">
                            <Form.Control
                              autoComplete="off"
                              type="text"
                              value={newLeaderboardName}
                              onChange={(e) => setNewLeaderboardName(e.target.value)}
                              placeholder={t(prefix + 'leaderboardNamePlaceholder')}
                              onKeyDown={(e) => e.key === 'Enter' && handleCreateLeaderboard()}
                            />
                            <CustomButton
                              btnText={t(prefix + 'createLeaderboard')}
                              onClick={handleCreateLeaderboard}
                            />
                          </div>
                        )}

                        {/* List existing leaderboards */}
                        <div className="organisation-tours__leaderboard-list">
                          {leaderboards && leaderboards.length > 0 ? (
                            leaderboards.map((lb) => (
                              <div key={lb.id} className="organisation-tours__leaderboard-item">
                                {editingLeaderboardId === lb.id ? (
                                  <div className="organisation-tours__leaderboard-edit">
                                    <Form.Control
                                      autoComplete="off"
                                      type="text"
                                      value={editLeaderboardName}
                                      onChange={(e) => setEditLeaderboardName(e.target.value)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') handleUpdateLeaderboard(lb.id);
                                        if (e.key === 'Escape') cancelEditLeaderboard();
                                      }}
                                      autoFocus
                                    />
                                    <div className="organisation-tours__leaderboard-actions">
                                      <button
                                        type="button"
                                        className="organisation-tours__leaderboard-action-btn organisation-tours__leaderboard-action-btn--save"
                                        onClick={() => handleUpdateLeaderboard(lb.id)}
                                        title={t(prefix + 'saveTour')}
                                      >
                                        <Check />
                                      </button>
                                      <button
                                        type="button"
                                        className="organisation-tours__leaderboard-action-btn organisation-tours__leaderboard-action-btn--cancel"
                                        onClick={cancelEditLeaderboard}
                                        title={t(prefix + 'cancel')}
                                      >
                                        <X />
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="organisation-tours__leaderboard-view">
                                    <span className="organisation-tours__leaderboard-name">
                                      {lb.name}
                                    </span>
                                    {isOwner && (
                                      <div className="organisation-tours__leaderboard-actions">
                                        <button
                                          type="button"
                                          className="organisation-tours__leaderboard-action-btn"
                                          onClick={() => startEditLeaderboard(lb)}
                                          title={t(prefix + 'edit')}
                                        >
                                          <Pencil />
                                        </button>
                                        <button
                                          type="button"
                                          className="organisation-tours__leaderboard-action-btn organisation-tours__leaderboard-action-btn--delete"
                                          onClick={() => handleDeleteLeaderboard(lb.id)}
                                          title={t(prefix + 'delete')}
                                        >
                                          <Trash />
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            ))
                          ) : (
                            <div className="organisation-tours__empty">
                              {t(prefix + 'noLeaderboards')}
                            </div>
                          )}
                        </div>
                      </>
                    )}
                    {isCreatingNew && (
                      <div className="organisation-tours__tip">
                        {t(prefix + 'saveToAddLeaderboards')}
                      </div>
                    )}
                  </Card.Body>
                </Accordion.Collapse>
              </Card>

              <Card>
                <Card.Header className="p-0 bg-white border-0 relative">
                  <CustomAccordionHeader eventKey="tournaments">
                    {t(prefix + 'tournaments')}
                  </CustomAccordionHeader>
                </Card.Header>
                <Accordion.Collapse eventKey="tournaments">
                  <Card.Body className="organisation-tours__tool-body">
                    <div className="organisation-tours__tip">{t(prefix + 'tournamentsTip')}</div>
                    <div className="organisation-tours__tournaments-list">
                      {!tournamentsLoaded ? (
                        <div className="organisation-tours__empty">...</div>
                      ) : getTournamentsForTour(selectedTour?.id || null).length > 0 ? (
                        getTournamentsForTour(selectedTour?.id || null).map((tournament) => {
                          const isOwn = isOwnTournament(tournament.id);
                          const content = (
                            <>
                              <div className="organisation-tours__tournament-name">
                                {tournament.name}
                                {!isOwn && (
                                  <span className="organisation-tours__tournament-external-badge">
                                    {t(prefix + 'externalTournament')}
                                  </span>
                                )}
                              </div>
                              <div className="organisation-tours__tournament-dates">
                                {tournament.startDate && tournament.endDate
                                  ? `${new Date(
                                      tournament.startDate
                                    ).toLocaleDateString()} - ${new Date(
                                      tournament.endDate
                                    ).toLocaleDateString()}`
                                  : t(prefix + 'noDates')}
                              </div>
                            </>
                          );
                          return isOwn ? (
                            <a
                              key={tournament.id}
                              className="organisation-tours__tournament-item organisation-tours__tournament-item--clickable"
                              href={`/community/${currentOrganization?.id}/tournament/${tournament.id}/tags/dashboard`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {content}
                            </a>
                          ) : (
                            <div
                              key={tournament.id}
                              className="organisation-tours__tournament-item organisation-tours__tournament-item--external"
                            >
                              {content}
                            </div>
                          );
                        })
                      ) : (
                        <div className="organisation-tours__empty">
                          {t(prefix + 'noTournaments')}
                        </div>
                      )}
                    </div>
                  </Card.Body>
                </Accordion.Collapse>
              </Card>

              <Card>
                <Card.Header className="p-0 bg-white border-0 relative">
                  <CustomAccordionHeader eventKey="tieBreakers">
                    {t(prefix + 'tieBreakers')}
                  </CustomAccordionHeader>
                </Card.Header>
                <Accordion.Collapse eventKey="tieBreakers">
                  <Card.Body className="organisation-tours__tool-body">
                    <div className="organisation-tours__form-section">
                      <GroupStandingsTieBreakerSection
                        value={
                          tourFormData.useInheritedGroupStandingsTieBreakers
                            ? tourTieBreakerResolution.effective
                            : tourFormData.groupStandingsTieBreakers || []
                        }
                        description={t('groupStandingsTieBreaker-description')}
                        onChange={(next) => {
                          setHasCustomized(true);
                          setSelectedPresetId(null);
                          setTourFormData((prev) => ({
                            ...prev,
                            groupStandingsTieBreakers: next,
                          }));
                        }}
                        
                        inheritChecked={Boolean(tourFormData.useInheritedGroupStandingsTieBreakers)}
                        
                        onInheritChange={(checked) => {
                          setHasCustomized(true);
                          setSelectedPresetId(null);
                          setTourFormData((prev) => ({
                            ...prev,
                            useInheritedGroupStandingsTieBreakers: checked,
                            
                            groupStandingsTieBreakers: checked
                              ? resolveInheritedTourTieBreakers(
                                currentOrganization?.groupStandingsTieBreakers,
                              )
                              : prev.groupStandingsTieBreakers,
                          }));
                        }}
                        inheritLabelKey="groupStandingsTieBreaker-useInheritedFromOrganization"
                        
                        editorDisabled={
                          !isOwner || 
                          Boolean(tourFormData.useInheritedGroupStandingsTieBreakers)
                        }
                        
                        inheritDisabled={!isOwner}
                        
                        resolution={tourTieBreakerResolution}
                        chainContextScope="tour"
                        scoringValue={
                          tourFormData.useInheritedTeamMatchupScoring
                            ? tourTieBreakerResolution.teamMatchupScoring
                            : tourFormData.teamMatchupScoring
                        }
                        onScoringChange={(next) => {
                          setHasCustomized(true);
                          setTourFormData((prev) => ({
                            ...prev,
                            teamMatchupScoring: next,
                          }));
                        }}
                        inheritScoringChecked={Boolean(tourFormData.useInheritedTeamMatchupScoring)}
                        onInheritScoringChange={(checked) => {
                          setHasCustomized(true);
                          setTourFormData((prev) => ({
                            ...prev,
                            useInheritedTeamMatchupScoring: checked,
                            teamMatchupScoring: checked
                              ? tourTieBreakerResolution.teamMatchupScoring ??
                                DEFAULT_TEAM_MATCHUP_SCORING
                              : prev.teamMatchupScoring,
                          }));
                        }}
                        inheritScoringLabelKey="teamMatchupScoring-inheritFromOrganization"
                      />
                    </div>
                  </Card.Body>
                </Accordion.Collapse>
              </Card>

              {/* TODO: If we implement so you can invite other users to be managers of a tour, add this back. */}
              {/* <Card>
              <Card.Header className="p-0 bg-white border-0 relative">
                <CustomAccordionHeader eventKey="users">
                  {t(prefix + 'organizationsWithAccess')}
                </CustomAccordionHeader>
              </Card.Header>
              <Accordion.Collapse eventKey="users">
                <Card.Body className="organisation-tours__tool-body">
                  {selectedTour ? (
                    (() => {
                      const organizations = getTourOrganizations(selectedTour.id);
                      return (
                        <>
                          <div className="mb-3 d-flex align-items-center">
                            <span className="me-2">{t(prefix + 'organizationsWithAccess')}</span>
                            <OverlayTrigger
                              placement="top"
                              overlay={
                                <Tooltip>
                                  <div>
                                    <div className="mb-2">
                                      <span className="organisation-tours__role-dot organisation-tours__role-dot--owner me-2"></span>
                                      <strong>{t(prefix + 'owner')}:</strong>{' '}
                                      {t(prefix + 'ownerPermission')}
                                    </div>
                                    <div>
                                      <span className="organisation-tours__role-dot organisation-tours__role-dot--manager me-2"></span>
                                      <strong>{t(prefix + 'manager')}:</strong>{' '}
                                      {t(prefix + 'managerPermission')}
                                    </div>
                                  </div>
                                </Tooltip>
                              }
                            >
                              <InfoCircleFill style={{ cursor: 'pointer' }} />
                            </OverlayTrigger>
                          </div>
                          {organizations.length > 0 ? (
                            <div className="organisation-tours__organizations-list">
                              {organizations.map((org: AdminTourOrganizationAccessDto) => (
                                <div key={org.id} className="organisation-tours__owner-display">
                                  <span
                                    className={classnames('organisation-tours__role-dot', {
                                      'organisation-tours__role-dot--owner': org.isOwner,
                                      'organisation-tours__role-dot--manager': !org.isOwner,
                                    })}
                                  ></span>
                                  <span>{org.name}</span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="organisation-tours__error">
                              {t(prefix + 'noOrganizationsFound')}
                            </div>
                          )}
                          <div className="mt-3">
                            <CustomButton
                              btnText={t(prefix + 'inviteOrganization')}
                              onClick={() => {
                                // TODO: Implement invite organization functionality when backend is ready
                                console.log('Invite organization clicked');
                              }}
                              disabled={!isOwner}
                            />
                          </div>
                        </>
                      );
                    })()
                  ) : (
                    <div className="organisation-tours__error">
                      {t(prefix + 'noOrganizationsFound')}
                    </div>
                  )}
                </Card.Body>
              </Accordion.Collapse>
            </Card> */}
            </Accordion>
          </>
        )}
        <ToastContainer />
      </div>
    </div>
  );
};
